import TableHeaderCell from '../Engine/Common/TableHeaderCell/TableHeaderCell';
import { TableCell } from './styled';
import moment from 'moment';
import { LONG_DASH } from '../EngineVersionUtils';
import {
  ENGINE_VERSION_1_0,
  ENGINE_VERSION_BASE,
  EngineConfigStatus,
  EngineUpdatedFrom,
} from '@/store/Engine/constants';
import { Skeleton, Tooltip } from 'antd';
import ReportsSvg from '@/assets/Report.svg?react';

import { EPButton } from '@/components';
import { t } from 'i18next';
import { EngineStore } from '@/store/Engine/EngineStore';
import { useTranslation } from 'react-i18next';

export const showRevertButton = (rowData, engineStore: EngineStore): boolean => {
  const publishedVersion = engineStore.publishedEngineConfig?.version;

  const isPublishedStatus = rowData.configurationStatus === EngineConfigStatus.Published;
  const isDifferentVersion = rowData.version !== publishedVersion;
  const isNotVersion1 = engineStore.engineVersion !== ENGINE_VERSION_1_0;

  return isPublishedStatus && isDifferentVersion && isNotVersion1;
};

export const getVersionHistoryColumns = ({ permissionStore, engineStore, onConfirmationModalOpen, permissions }) => [
  {
    dataIndex: 'updatedDate',
    key: 'updatedDate',
    title: <TableHeaderCell titleKey="lastUpdated" className="header-cell" />,
    render: updatedDate => (
      <TableCell>
        {engineStore.isVersionHistoryLoading ? (
          <Skeleton.Input active />
        ) : updatedDate ? (
          moment(updatedDate).format('ddd DD MMM YYYY HH:mm')
        ) : (
          LONG_DASH
        )}
      </TableCell>
    ),
  },
  {
    dataIndex: 'version',
    key: 'version',
    title: <TableHeaderCell titleKey="version" className="header-cell" />,
    render: version => (
      <TableCell>
        {engineStore.isVersionHistoryLoading ? (
          <Skeleton.Input active />
        ) : version ? (
          getFormattedEngineVersion(version)
        ) : (
          LONG_DASH
        )}
      </TableCell>
    ),
  },
  {
    dataIndex: 'configurationStatus',
    key: 'configurationStatus',
    title: <TableHeaderCell titleKey="Status" className="header-cell" />,
    render: configurationStatus => (
      <TableCell>
        {engineStore.isVersionHistoryLoading ? (
          <Skeleton.Input active />
        ) : configurationStatus ? (
          t(configurationStatus)
        ) : (
          LONG_DASH
        )}
      </TableCell>
    ),
  },
  {
    dataIndex: 'updatedByUserEmail',
    key: 'updatedByUserEmail',
    title: <TableHeaderCell titleKey="User" className="header-cell" />,
    render: updatedByUserEmail => (
      <TableCell>
        {engineStore.isVersionHistoryLoading ? <Skeleton.Input active /> : updatedByUserEmail ?? LONG_DASH}
      </TableCell>
    ),
  },
  {
    dataIndex: 'updatedFrom',
    key: 'updatedFrom',
    title: <TableHeaderCell titleKey="Source" className="header-cell" />,
    render: (_, { updatedFrom }) => (
      <TableCell>
        {engineStore.isVersionHistoryLoading ? (
          <Skeleton.Input active />
        ) : updatedFrom ? (
          t(updatedFrom === EngineUpdatedFrom.Draft ? 'manual' : 'algo')
        ) : (
          LONG_DASH
        )}
      </TableCell>
    ),
  },
  {
    dataIndex: 'comments',
    key: 'comments',
    title: <TableHeaderCell titleKey="comments" className="header-cell" />,
    render: (_, { comments }) => (
      <TableCell>{engineStore.isVersionHistoryLoading ? <Skeleton.Input active /> : comments ?? LONG_DASH}</TableCell>
    ),
  },
  {
    dataIndex: 'approvalReport',
    key: 'approvalReport',
    title: <TableHeaderCell titleKey="" className="header-cell" />,
    render: (_, rowData) => {
      return engineStore.isVersionHistoryLoading ? (
        <Skeleton.Input active />
      ) : (
        <ReportLink
          reportUrl={rowData.publishedConfig?.additionalInfo?.approvalReportUrl}
          updatedFrom={rowData?.updatedFrom}
          isLoading={engineStore.isVersionHistoryLoading}
        />
      );
    },
  },
  {
    dataIndex: 'id',
    key: 'id',
    title: <TableHeaderCell titleKey="" className="header-cell" />,
    render: (id: string, rowData) => {
      return (
        <TableCell>
          {engineStore.isVersionHistoryLoading ? (
            <Skeleton.Input active />
          ) : (
            showRevertButton(rowData, engineStore) && (
              <EPButton
                disabled={!permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE) || engineStore.hasPIDChanges || engineStore.hasKpiChanges}
                type="primary"
                size="medium"
                className="decline-button"
                onClick={e => {
                  e.stopPropagation();
                  onConfirmationModalOpen(rowData);
                }}
              >
                {t('Revert')}
              </EPButton>
            )
          )}
        </TableCell>
      );
    },
  },
];

export const onPublishPreviousVersion =
  ({ selectedVersion, tvalue, engineStore, setIsLoadingRevert, setIsModelOpen, setModalText }) =>
  async () => {
    try {
      setIsLoadingRevert(true);
      await engineStore.getValidEngineRevertDetails(selectedVersion.id);

      if (!engineStore.validEngineRevertData.isValidEngineRevert) {
        setModalText(engineStore.validEngineRevertData.message);
        setIsLoadingRevert(false);
      } else {
        await engineStore.publishPreviousVersion(selectedVersion.id, tvalue);
        setIsLoadingRevert(false);
      }
    } catch (error) {
      setIsLoadingRevert(false);
    } finally {
      setIsModelOpen(false);
    }
  };

export const handleVersionClick = ({ rowData, engineStore, tvalue, setSelectedModalRow, setIsViewModalOpen }) => {
  rowData.publishedConfig.configurationStatus = EngineConfigStatus.Published;
  engineStore.setEngineConfig(rowData?.publishedConfig, tvalue);
  setSelectedModalRow({
    versionNumber: rowData.version,
    status: rowData.configurationStatus,
  });

  setIsViewModalOpen(true);
};

export const getFormattedEngineVersion = (version: string) => `${ENGINE_VERSION_BASE}${version}`;

const ReportLink = ({
  reportUrl,
  updatedFrom,
  isLoading,
}: {
  reportUrl?: string;
  updatedFrom?: string;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton.Input active />;
  }

  if (!reportUrl || updatedFrom !== 'algo') {
    return <>{LONG_DASH}</>;
  }

  return (
    <a href={reportUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
      <Tooltip title={t('viewReport')}>
        <ReportsSvg />
      </Tooltip>
    </a>
  );
};

export const emptyData = [
  {
    id: 1,
    version: '',
    configurationStatus: '',
    updatedDate: '',
    updatedBy: '',
  },
];
